import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Plus, Trash2, Filter, Save, X } from 'lucide-react';
import { toast } from 'sonner';

const OPERATORS = {
  text: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'starts_with', label: 'Starts With' },
  ],
  select: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
  ],
  date: [
    { value: 'equals', label: 'On' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
  ],
};

const AdvancedFilterBuilder = ({ 
  entityType, 
  fields, 
  onApply, 
  savedFilters = [],
  onSaveFilter,
  onDeleteFilter 
}) => {
  const [open, setOpen] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [logic, setLogic] = useState('AND');
  const [filterName, setFilterName] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const addCondition = () => {
    setConditions([...conditions, { id: Date.now(), field: '', operator: '', value: '' }]);
  };

  const removeCondition = (id) => {
    setConditions(conditions.filter(c => c.id !== id));
  };

  const updateCondition = (id, key, value) => {
    setConditions(conditions.map(c => c.id === id ? { ...c, [key]: value } : c));
  };

  const getFieldConfig = (fieldName) => fields.find(f => f.name === fieldName);

  const getOperatorsForField = (fieldName) => {
    const field = getFieldConfig(fieldName);
    if (!field) return OPERATORS.text;
    return OPERATORS[field.type] || OPERATORS.text;
  };

  const handleApply = () => {
    const validConditions = conditions.filter(c => c.field && c.operator && c.value);
    if (validConditions.length === 0) {
      onApply(null);
      setOpen(false);
      return;
    }
    onApply({ logic, conditions: validConditions.map(c => ({ field: c.field, operator: c.operator, value: c.value })) });
    setOpen(false);
  };

  const handleSaveFilter = () => {
    if (!filterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }
    const filterData = {
      name: filterName,
      entity_type: entityType,
      logic,
      conditions: conditions.filter(c => c.field && c.operator && c.value).map(c => ({ field: c.field, operator: c.operator, value: c.value }))
    };
    if (onSaveFilter) onSaveFilter(filterData);
    setSaveDialogOpen(false);
    setFilterName('');
    toast.success('Filter saved');
  };

  const loadSavedFilter = (filter) => {
    setLogic(filter.logic || 'AND');
    const loadedConditions = [];
    for (let i = 0; i < filter.conditions.length; i++) {
      loadedConditions.push({ id: Date.now() + i, ...filter.conditions[i] });
    }
    setConditions(loadedConditions);
  };

  const clearFilters = () => {
    setConditions([]);
    setLogic('AND');
    onApply(null);
    setOpen(false);
  };

  // Helper functions to render lists without inline .map()
  const renderSavedFilters = () => {
    const items = [];
    for (let i = 0; i < savedFilters.length; i++) {
      const filter = savedFilters[i];
      items.push(
        <Badge key={filter.id} variant="outline" className="cursor-pointer hover:bg-primary/10" onClick={() => loadSavedFilter(filter)}>
          {filter.name}
          {onDeleteFilter && <X className="w-3 h-3 ml-1 hover:text-destructive" onClick={(e) => { e.stopPropagation(); onDeleteFilter(filter.id); }} />}
        </Badge>
      );
    }
    return items;
  };

  const renderFieldOptions = () => {
    const items = [];
    for (let i = 0; i < fields.length; i++) {
      items.push(<SelectItem key={fields[i].name} value={fields[i].name}>{fields[i].label}</SelectItem>);
    }
    return items;
  };

  const renderOperatorOptions = (fieldName) => {
    const operators = getOperatorsForField(fieldName);
    const items = [];
    for (let i = 0; i < operators.length; i++) {
      items.push(<SelectItem key={operators[i].value} value={operators[i].value}>{operators[i].label}</SelectItem>);
    }
    return items;
  };

  const renderSelectValueOptions = (fieldName) => {
    const field = getFieldConfig(fieldName);
    if (!field || !field.options) return null;
    const items = [];
    for (let i = 0; i < field.options.length; i++) {
      items.push(<SelectItem key={field.options[i].value} value={field.options[i].value}>{field.options[i].label}</SelectItem>);
    }
    return items;
  };

  const renderValueInput = (condition) => {
    const field = getFieldConfig(condition.field);
    if (field && field.type === 'select' && field.options) {
      return (
        <Select value={condition.value} onValueChange={(v) => updateCondition(condition.id, 'value', v)}>
          <SelectTrigger className="flex-1"><SelectValue placeholder="Select value" /></SelectTrigger>
          <SelectContent>{renderSelectValueOptions(condition.field)}</SelectContent>
        </Select>
      );
    }
    if (field && field.type === 'date') {
      return <Input type="date" value={condition.value} onChange={(e) => updateCondition(condition.id, 'value', e.target.value)} className="flex-1" />;
    }
    return <Input value={condition.value} onChange={(e) => updateCondition(condition.id, 'value', e.target.value)} placeholder="Value" className="flex-1" />;
  };

  const renderConditions = () => {
    const items = [];
    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];
      items.push(
        <Card key={condition.id} className="border-dashed">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground w-8">{i === 0 ? 'If' : logic}</span>
              <Select value={condition.field} onValueChange={(v) => updateCondition(condition.id, 'field', v)}>
                <SelectTrigger className="w-[150px]" data-testid={`filter-field-${i}`}><SelectValue placeholder="Field" /></SelectTrigger>
                <SelectContent>{renderFieldOptions()}</SelectContent>
              </Select>
              <Select value={condition.operator} onValueChange={(v) => updateCondition(condition.id, 'operator', v)}>
                <SelectTrigger className="w-[130px]" data-testid={`filter-operator-${i}`}><SelectValue placeholder="Operator" /></SelectTrigger>
                <SelectContent>{renderOperatorOptions(condition.field)}</SelectContent>
              </Select>
              {renderValueInput(condition)}
              <Button variant="ghost" size="icon" onClick={() => removeCondition(condition.id)} data-testid={`remove-condition-${i}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }
    return items;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="advanced-filter-btn">
          <Filter className="w-4 h-4 mr-2" />
          Advanced Filter
          {conditions.length > 0 && <Badge variant="secondary" className="ml-2">{conditions.length}</Badge>}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Filter className="w-5 h-5" />Advanced Filter Builder</DialogTitle>
          <DialogDescription>Build complex filters with AND/OR logic</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {savedFilters.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Saved Filters</Label>
              <div className="flex flex-wrap gap-2">{renderSavedFilters()}</div>
            </div>
          )}
          <div className="flex items-center gap-4">
            <Label>Match</Label>
            <Select value={logic} onValueChange={setLogic}>
              <SelectTrigger className="w-[120px]" data-testid="filter-logic"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AND">ALL (AND)</SelectItem>
                <SelectItem value="OR">ANY (OR)</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">of the following conditions</span>
          </div>
          <ScrollArea className="max-h-[300px]"><div className="space-y-3">{renderConditions()}</div></ScrollArea>
          <Button variant="outline" onClick={addCondition} className="w-full" data-testid="add-condition-btn">
            <Plus className="w-4 h-4 mr-2" />Add Condition
          </Button>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-1">
            <Button variant="ghost" onClick={clearFilters}>Clear All</Button>
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={conditions.length === 0}><Save className="w-4 h-4 mr-2" />Save Filter</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader><DialogTitle>Save Filter</DialogTitle><DialogDescription>Give your filter a name for quick access</DialogDescription></DialogHeader>
                <div className="py-4">
                  <Label>Filter Name</Label>
                  <Input value={filterName} onChange={(e) => setFilterName(e.target.value)} placeholder="e.g., My Pending Applications" className="mt-2" data-testid="filter-name-input" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleSaveFilter} data-testid="confirm-save-filter">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Button onClick={handleApply} data-testid="apply-filter-btn">Apply Filter</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedFilterBuilder;
